import { useState, useEffect, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ResponseError } from '../../types/ResponseError';
import { useAppSelector } from '../../app/hooks';
import { selectUser } from '../auth/authSlice';
import { canViewTimeTracker } from '../../common/utils/permissionUtils';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDayjs from '@mui/lab/AdapterDayjs';
import FormHelperText from '@mui/material/FormHelperText';
import { useDeleteTimeTrackerMutation, useLazyGetTimeTrackersQuery } from '../../app/services/interview';
import dayjs from 'dayjs';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DefaultPageSize } from '../../common/utils/commonUtils';
import { useGetCoCMPatientsInAccountQuery } from '../../app/services/patient';
import TimeTrackerResult from './TimeTrackerResult';
import { getPatientFullNameForDropwDown } from '../../common/utils/patientUtils';
import { TimeTrackerSearch } from '../../common/utils/timeTracker';
import LocalPopupModal from '../../common/components/LocalPopupModal';
import { PopupContent } from '../../types/PopupType';
import TimeTrackerForm from './TimeTrackerForm';
import { TimeTrackerResponseItem } from '../../types/TimeTracker';
import { useToast } from '../../app/toast';
import RoundedContainer from '../../common/components/RoundedContainer';

export interface TimeTrackerSearchRequest {
  patientId?: string;
  fromDate?: Date;
  toDate?: Date;
}

export interface TimeTrackerDownloadRequest extends TimeTrackerSearchRequest { }

let defaultFormValues: TimeTrackerSearchRequest = {
  patientId: '',
  fromDate: undefined,
  toDate: undefined,
};

const StyleAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '49%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const StyleDesktopDatePicker = styled(DesktopDatePicker)(({ theme }) => ({
  width: '49%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const validationDate = ({
  fromDate,
  toDate
}: {
  fromDate?: Date;
  toDate?: Date;
}) => {
  if ((fromDate && !toDate) || (!fromDate && toDate)) {
    return true;
  }
  if (fromDate && toDate) {
    const fromDateFormat = new Date(new Date(fromDate).toISOString().split('T')[0]);
    const toDateFormat = new Date(new Date(toDate).toISOString().split('T')[0]);
    if (fromDateFormat.getTime() > toDateFormat.getTime()) {
      return false;
    }
  }
  return true;
};

export default function TimeTrackerFilterForm() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectUser);
  const toast = useToast();

  const matches = useMediaQuery(theme.breakpoints.down('md'));

  const [searchParams] = useSearchParams();
  const params = useParams();

  const [patientId, setPatientId] = useState<string>(params.patientId || '');
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [timeTrackerId, setTimeTrackerId] = useState<number | undefined>(undefined);
  const [timeTracker, setTimeTracker] = useState<TimeTrackerResponseItem>();
  const [defaultTimeTrackerLoaded, setDefaultTimeTrackerLoaded] = useState<boolean>(false);

  const currentStartDate = dayjs().startOf("month").format("MM/DD/YYYY");
  const currentEndDate = dayjs().endOf("month").format("MM/DD/YYYY");

  const isValidInputDate = (inputDate: string) => dayjs(inputDate, 'MM/DD/YYYY').isValid();

  const searchOptions = {
    patientId,
    fromDate: searchParams.get('fromDate') || currentStartDate,
    toDate: searchParams.get('toDate') || currentEndDate,
    page: searchParams.get('page') || '1',
    pageSize: searchParams.get('pageSize') || DefaultPageSize,
    sort: searchParams.get('sort') || 'trackerMonth',
    dir: searchParams.get('dir') || 'desc',
  };

  defaultFormValues = {
    patientId: searchOptions.patientId,
    fromDate: isValidInputDate(searchOptions.fromDate) ? new Date(searchOptions.fromDate) : undefined,
    toDate: isValidInputDate(searchOptions.fromDate) ? new Date(searchOptions.toDate) : undefined,
  };

  const [fromDate, setFromDate] = useState<any>(isValidInputDate(searchOptions.fromDate) ? dayjs(searchOptions.fromDate) : null);
  const [toDate, setToDate] = useState<any>(isValidInputDate(searchOptions.toDate) ? dayjs(searchOptions.toDate) : null);
  const [fromDateError, setFromDateError] = useState<string>('');
  const [toDateError, setToDateError] = useState<string>('');
  const [processedPatients, setProcessedPatients] = useState<Array<{ patientId: string; fullName: string, externalId?: string }>>([]);
  const [disableAddTime, setDisableAddTime] = useState<boolean>(patientId === TimeTrackerSearch.Patient.All);

  const canManageTimeTracker = canViewTimeTracker(currentUser.roleId);
  const { data: patientData } = useGetCoCMPatientsInAccountQuery(undefined, { skip: !canManageTimeTracker });

  const [triggerGetTimeTrackers, { data: timeTrackersData }] = useLazyGetTimeTrackersQuery();
  const [deleteTimeTrackerMutation] = useDeleteTimeTrackerMutation();
  const [isOpenTimeTrackerPopup, setIsOpenTimeTrackerPopup] = useState<boolean>(false);
  const [isOpenDeleteTimeTrackerPopup, setIsOpenDeleteTimeTrackerPopup] = useState<boolean>(false);

  const timeTrackerContentPopup = {
    title: timeTrackerId ? t('timeTracker.editTime') : t('timeTracker.add'),
  } as PopupContent;

  const showTimeTrackerPopup = async () => {
    setIsOpenTimeTrackerPopup(true);
  };

  const closeTimeTrackerPopup = async () => {
    setIsOpenTimeTrackerPopup(false);
  };

  const showDeleteTimeTrackerPopup = async () => {
    setIsOpenDeleteTimeTrackerPopup(true);
  };

  const closeDeleteTimeTrackerPopup = async () => {
    setIsOpenDeleteTimeTrackerPopup(false);
  };

  // ***************************** Delete Popup *****************************
  const deleteContentPopup = {
    title: t('timeTracker.deletePopupContent.title'),
    description: `${t('timeTracker.deletePopupContent.description')}`,
    btnOk: t('timeTracker.deletePopupContent.btnOk'),
    btnClose: t('timeTracker.deletePopupContent.btnClose'),
    toastMessage: t('timeTracker.deletePopupContent.toastMessage'),
  } as PopupContent;

  const showDeletePopup = (id: number) => {
    setTimeTrackerId(id);
    showDeleteTimeTrackerPopup();
  };

  const closeDeletePopup = async () => {
    closeDeleteTimeTrackerPopup();
  };

  const onTimeTrackerFormSubmitCallback = async (
    result: boolean
  ) => {
    if (result) {
      setIsOpenTimeTrackerPopup(false);
      const isValid = await trigger(); // runs validation
      if (isValid) {
        handleSubmit(onSubmit)(); // manually submit
      }
    }
  };

  useEffect(() => {
    if (patientData?.patients) {
      const updatedPatients = patientData.patients
        .map((patient) => ({
          ...patient,
          fullName: getPatientFullNameForDropwDown(patient)
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName));
      const allPatients = [{ patientId: TimeTrackerSearch.Patient.All, fullName: TimeTrackerSearch.Patient.AllText }, ...updatedPatients];
      setProcessedPatients(allPatients || []);

    }
  }, [patientData]);

  const validationSchema = yup
    .object({
      patientId: yup.string().trim()
        .test(
          'validate-patient',
          t('timeTracker.error.blankPatient'),
          function (patientId) {
            return patientId !== '';
          }
        ),
      fromDate: yup
        .date()
        .required(t('timeTracker.error.blankFromDate'))
        .typeError(t('timeTracker.error.blankFromDate'))
        .test('from-date', '', function (fromDateValue) {
          const toDateValue = getValues('toDate') || undefined;
          const isValidDate = isValidInputDate(fromDate);
          if (!isValidDate && fromDateValue) {
            return false;
          }
          if (fromDateValue) {
            const isValidDate = validationDate({
              fromDate: fromDateValue,
              toDate: toDateValue
            });

            if (!isValidDate) {
              setFromDateError(t('timeTracker.error.inValidDateRange'));
              return false;
            }
          }
          setFromDateError('');
          return true;
        })
        .nullable().transform((curr, orig) => orig === '' ? null : curr),

      toDate: yup
        .date()
        .required(t('timeTracker.error.blankToDate'))
        .typeError(t('timeTracker.error.blankToDate'))
        .test('from-date', '', function (toDateValue) {
          const fromDateValue = getValues('fromDate') || undefined;
          const isValidDate = isValidInputDate(toDate);
          if (!isValidDate && toDateValue) {
            return false;
          }
          if (toDateValue) {
            const isValidDate = validationDate({
              fromDate: fromDateValue,
              toDate: toDateValue
            });

            if (!isValidDate) {
              setToDateError(t('timeTracker.error.inValidToDateRange'));
              return false;
            }
          }
          setToDateError('');
          return true;
        })
        .nullable().transform((curr, orig) => orig === '' ? null : curr)
    })
    .required();

  const {
    control,
    formState: { errors },
    handleSubmit,
    trigger,
    setValue,
    getValues,
  } = useForm<TimeTrackerSearchRequest>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultFormValues,
  });

  const handlePatientChange = (e: any, values: any) => {
    const selectedValue = values ? values.patientId : '';
    setDisableAddTime(selectedValue === TimeTrackerSearch.Patient.All)
    setPatientId(selectedValue);
    setValue('patientId', selectedValue);
  };

  const triggerTimeTrackerQuery = useCallback(
    async (searchRequest: any) => {
      const searchParam = { ...searchRequest };
      const url = generatePath(
        `/dashboard/patient/:patientId/time-tracker?patientId=:patientId&page=:page&pageSize=:pageSize&fromDate=:fromDate&toDate=:toDate&sort=:sort&dir=:dir`,
        {
          ...searchParam
        }
      );
      navigate(url, { replace: true });

      triggerGetTimeTrackers(searchRequest);
    },
    [navigate, triggerGetTimeTrackers]
  );

  const addTimeTracker = async () => {
    const isValid = await trigger(); // runs validation
    if (isValid) {
      setTimeTrackerId(undefined);
      setTimeTracker(undefined);
      showTimeTrackerPopup();
    }
  };

  const editTimeTracker = async (timeTracker: TimeTrackerResponseItem) => {
    setTimeTrackerId(timeTracker?.id);
    setTimeTracker(timeTracker);
    showTimeTrackerPopup();
  };


  const deleteTimeTracker = async () => {
    try {
      await deleteTimeTrackerMutation({ id: timeTrackerId }).unwrap();

      toast.publish(t('timeTracker.deletePopupContent.toastMessage'), 'success');

    } catch (e) {
      toast.publish(t('timeTracker.deletePopupConten.fail'), 'error');
    }
    closeDeletePopup();

    onTimeTrackerFormSubmitCallback(true);
  };

  const getPatientOptionLabel = (patient: any) => patient?.fullName;

  const onSubmit = async (data: TimeTrackerSearchRequest) => {
    try {
      const searchRequest = {
        patientId: data.patientId,
        fromDate: fromDate ? dayjs(fromDate).format('MM/DD/YYYY') : undefined,
        toDate: toDate ? dayjs(toDate).format('MM/DD/YYYY') : undefined,
        timezoneOffset: new Date().getTimezoneOffset(),
        page: 1,
        pageSize: searchOptions.pageSize,
        sort: searchOptions.sort,
        dir: searchOptions.dir,
      }
      triggerTimeTrackerQuery(searchRequest);

    } catch (e) {
      const {
        data: { error },
      } = e as ResponseError;
      setPageErrors(error);
    }
  };

  const viewReport = async (patientId?: string) => {
    const isValid = await trigger(); // runs validation
    if (isValid) {
      const searchRequest = {
        patientId,
        fromDate: fromDate ? dayjs(fromDate).format('MM/DD/YYYY') : undefined,
        toDate: toDate ? dayjs(toDate).format('MM/DD/YYYY') : undefined,
        sort: searchOptions.sort,
        dir: searchOptions.dir,
      }
      window.open(`/dashboard/time-tracker-report?patientId=${patientId}&fromDate=${searchRequest.fromDate}&toDate=${searchRequest.toDate}`, '_blank');
    }
  };

  useEffect(() => {
    if (patientId && fromDate && toDate && !defaultTimeTrackerLoaded) {
      setDefaultTimeTrackerLoaded(true);
      const searchRequest = {
        patientId: patientId,
        fromDate: fromDate ? dayjs(fromDate).format('MM/DD/YYYY') : undefined,
        toDate: toDate ? dayjs(toDate).format('MM/DD/YYYY') : undefined,
        timezoneOffset: new Date().getTimezoneOffset(),
        page: 1,
        pageSize: searchOptions.pageSize,
        sort: searchOptions.sort,
        dir: searchOptions.dir,
      }
      triggerTimeTrackerQuery(searchRequest);
    }
  }, [patientId, fromDate, toDate, searchOptions.pageSize, searchOptions.sort, searchOptions.dir, triggerTimeTrackerQuery, defaultTimeTrackerLoaded]);

  return (
    <>
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          px: matches ? 0 : 3,
          pt: 3,
          pl: 0,
          pr: 0,
        }}
      >
        <RoundedContainer sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <Controller
              name="patientId"
              control={control}
              defaultValue={patientId}
              render={({ fieldState, formState, field: { value }, ...props }) => (
                <StyleAutocomplete
                  {...props}
                  data-testid="mui-component-select-patient"
                  options={processedPatients || []}
                  getOptionLabel={getPatientOptionLabel}
                  filterOptions={(options, { inputValue }) =>
                    options.filter(
                      (o: any) =>
                        `${o.fullName || ''}`.toLowerCase().includes(inputValue.toLowerCase()) ||
                        (o.externalId || '').toLowerCase().includes(inputValue.toLowerCase())
                    )
                  }
                  onChange={handlePatientChange}
                  value={
                    processedPatients?.find((item) => item.patientId === value) || null
                  }
                  renderOption={(props, option: any) => {
                    return (
                      <li {...props} key={option.patientId} value={option.patientId}>
                        {getPatientOptionLabel(option)}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.patientId}
                      helperText={errors.patientId?.message}
                      margin="normal"
                      fullWidth
                      label={t('timeTracker.patient')}
                    />
                  )}
                />
              )}
            />

            <Button
              type="button"
              disabled={disableAddTime}
              onClick={addTimeTracker}
              fullWidth
              variant="contained"
              sx={{
                width: matches ? '100%' : '24%',
                my: 2,
                py: 2,
                float: 'right',
                height: 56,
              }}
            >
              {t('timeTracker.add')}
            </Button>
          </Box>
        </RoundedContainer>
        <RoundedContainer>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '49%',
                  [theme.breakpoints.down('md')]: {
                    width: '100%',
                  },
                }}
              >
                <Controller
                  name="fromDate"
                  control={control}
                  render={({ field }) => (
                    <StyleDesktopDatePicker
                      {...field}
                      label="From the first day of"
                      views={['year', 'month']}
                      inputFormat="MM/YYYY"
                      value={fromDate}
                      onChange={(newDateValue: any, inputValue: any) => {
                        if (newDateValue) {
                          const isValidDate = dayjs(newDateValue).isValid();
                          if (!isValidDate) {
                            setFromDateError(t('timeTracker.error.inValidFromDate'));
                          } else {
                            // Set to first day of the selected month
                            const firstDayOfMonth = dayjs(newDateValue).startOf('month');
                            setValue('fromDate', firstDayOfMonth.toDate());
                            setFromDate(firstDayOfMonth);
                            setFromDateError('');
                          }
                        } else {
                          setValue('fromDate', newDateValue);
                          setFromDate(null);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          data-testid="mui-component-from-date"
                          sx={{
                            width: '100%',
                          }}
                          error={!!errors.fromDate || !!fromDateError}
                          helperText={errors.fromDate?.message}
                          margin="normal"
                          variant="outlined"
                          aria-describedby="from-date-helper-text"
                        />
                      )}
                    />
                  )}
                />
                <FormHelperText id="from-date-helper-text" error={true}>
                  {fromDateError}
                </FormHelperText>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '49%',
                  [theme.breakpoints.down('md')]: {
                    width: '100%',
                  },
                }}
              >
                <Controller
                  name="toDate"
                  control={control}
                  render={({ field }) => (
                    <StyleDesktopDatePicker
                      {...field}
                      label="To the last day of"
                      views={['year', 'month']}
                      inputFormat="MM/YYYY"
                      value={toDate}
                      onChange={(newDateValue: any, inputValue: any) => {
                        if (newDateValue) {
                          const isValidDate = dayjs(newDateValue).isValid();
                          if (!isValidDate) {
                            setToDateError(t('timeTracker.error.inValidToDate'));
                          } else {
                            // Set to last day of the selected month
                            const lastDayOfMonth = dayjs(newDateValue).endOf('month');
                            setValue('toDate', lastDayOfMonth.toDate());
                            setToDate(lastDayOfMonth);
                            setToDateError('');
                          }
                        } else {
                          setValue('toDate', newDateValue);
                          setToDate(null);
                        }
                      }}
                      renderInput={(params) =>
                        <TextField {...params}
                          data-testid="mui-component-to-date"
                          sx={{
                            width: '100%',
                          }}
                          error={!!errors.toDate || !!toDateError}
                          helperText={errors.toDate?.message}
                          margin="normal"
                          variant="outlined"
                          aria-describedby="to-date-helper-text"
                        />}
                    />
                  )}
                />
                <FormHelperText id="to-date-helper-text" error={true}>
                  {toDateError}
                </FormHelperText>
              </Box>
            </LocalizationProvider>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              pb: 3,
            }}
          >

            {pageErrors?.length > 0 &&
              pageErrors.map((error) => (
                <Alert key={error} severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              ))}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', width: '100%' }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  width: matches ? '100%' : '24%',
                  my: 2,
                  py: 2,
                }}
              >
                {t('timeTracker.runReport')}
              </Button>
              <Button
                type="button"
                fullWidth
                variant="contained"
                onClick={() => {
                  viewReport(patientId);
                }}
                sx={{
                  width: matches ? '100%' : '24%',
                  my: 2,
                  py: 2,
                }}
              >
                {t('timeTracker.saveToPdf')}
              </Button>
            </Box>
            {!matches && <Divider sx={{ py: 1 }} />}
          </Box>
        </RoundedContainer>
        <TimeTrackerResult
          timeTrackerData={timeTrackersData}
          triggerTimeTrackerQuery={triggerTimeTrackerQuery}
          editTimeTracker={editTimeTracker}
          deleteTimeTracker={showDeletePopup}
        />
      </Box>
      {(<>
        {/* Add Time Tracker Popup */}
        <LocalPopupModal
          isOpen={isOpenTimeTrackerPopup}
          contentPopup={timeTrackerContentPopup}
          onClose={closeTimeTrackerPopup}
          maxWidth={'lg'}
          fullWidth={true}
        >
          <TimeTrackerForm
            timeTracker={timeTracker}
            patientId={patientId}
            onTimeTrackerFormSubmitCallback={onTimeTrackerFormSubmitCallback}
          />
        </LocalPopupModal>

        {/* Delete Time Tracker Popup */}
        <LocalPopupModal
          isOpen={isOpenDeleteTimeTrackerPopup}
          contentPopup={deleteContentPopup}
          onSuccess={deleteTimeTracker}
          onClose={closeDeletePopup}
        />
      </>
      )}
    </>
  );
}
